import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Product } from '../../../types/product';
import { FormState, EditablePurchaseOrderItem, EditableHandlingUnit, ItemFieldErrors } from './types';
import DialogHeader from './DialogHeader';
import ItemsTable from './ItemsTable';

interface EditPurchaseOrderDialogProps {
  visible: boolean;
  onHide: () => void;
  editFormData: Partial<FormState>;
  setEditFormData: React.Dispatch<React.SetStateAction<Partial<FormState>>>;
  handleEditSubmit: () => void;
  handleRemoveHU: (itemIndex: number, huIndex: number) => void;
  handleAddHU: (itemIndex: number) => void;
  newHU: { huNumber?: string };
  setNewHU: (hu: { huNumber?: string }) => void;
  filteredProducts: Product[];
  searchProducts: (event: { query: string }) => Promise<void>;
}

interface ValidationErrors {
  poNumber?: string;
  notes?: string;
  /** Message for the whole items collection (e.g. "at least one item required"). */
  itemsGeneral?: string;
  /** Per-item field errors keyed by item index. */
  items: Record<number, ItemFieldErrors>;
}

const emptyErrors = (): ValidationErrors => ({ items: {} });

const toNumber = (v: string | number | undefined): number => {
  if (v === undefined || v === null || v === '') return NaN;
  return typeof v === 'number' ? v : parseFloat(v);
};

const EditPurchaseOrderDialog: React.FC<EditPurchaseOrderDialogProps> = ({
  visible,
  onHide,
  editFormData,
  setEditFormData,
  handleEditSubmit,
  handleRemoveHU,
  handleAddHU,
  newHU,
  setNewHU,
  filteredProducts,
  searchProducts,
}) => {
  const [errors, setErrors] = useState<ValidationErrors>(emptyErrors());
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [focusErrorsNonce, setFocusErrorsNonce] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useRef<Toast>(null);
  const initialSnapshotRef = useRef<string>('');

  // Capture a clean snapshot when the dialog opens, and reset validation state.
  useEffect(() => {
    if (visible) {
      initialSnapshotRef.current = JSON.stringify(editFormData);
      setValidationAttempted(false);
      setErrors(emptyErrors());
      setFocusErrorsNonce(0);
      setIsSubmitting(false);
    }
    // Only re-run when visibility toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const computeErrors = (): ValidationErrors => {
    const result: ValidationErrors = emptyErrors();

    if (!editFormData.poNumber?.trim()) {
      result.poNumber = 'PO Number is required';
    } else if (editFormData.poNumber.length > 50) {
      result.poNumber = 'PO Number cannot exceed 50 characters';
    }

    if (editFormData.notes?.length && editFormData.notes.length > 1000) {
      result.notes = 'Notes cannot exceed 1000 characters';
    }

    if (!editFormData.items?.length) {
      result.itemsGeneral = 'At least one item is required';
    } else {
      editFormData.items.forEach((item, index) => {
        const itemErrors: ItemFieldErrors = {};

        if (!item.itemNumber?.toString().trim()) {
          itemErrors.itemNumber = 'Required';
        }
        // Valid when a product is selected (productId > 0) OR a material number
        // is typed — the backend resolves-or-creates the product for a new
        // material, so a typed material with productId 0 is valid.
        const hasProduct = !!item.productId && item.productId > 0;
        const hasMaterial = !!item.material?.trim();
        if (!hasProduct && !hasMaterial) {
          itemErrors.material = 'Select or enter a material';
        }
        if (!(toNumber(item.quantity) > 0)) {
          itemErrors.quantity = 'Must be greater than 0';
        }
        if (!(toNumber(item.unitPrice) > 0)) {
          itemErrors.unitPrice = 'Must be greater than 0';
        }
        if (!item.deliveryDate) {
          itemErrors.deliveryDate = 'Required';
        }
        if (!item.shipToParty?.trim()) {
          itemErrors.shipToParty = 'Required';
        }

        if (Object.keys(itemErrors).length > 0) {
          result.items[index] = itemErrors;
        }
      });
    }

    return result;
  };

  const countProblems = (errs: ValidationErrors): number =>
    (errs.poNumber ? 1 : 0) +
    (errs.notes ? 1 : 0) +
    (errs.itemsGeneral ? 1 : 0) +
    Object.keys(errs.items).length;

  // Re-validate live once the user has attempted a save, so error markers clear
  // as fields are fixed (without ever showing a scary banner mid-edit).
  useEffect(() => {
    if (validationAttempted) {
      setErrors(computeErrors());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editFormData, validationAttempted]);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const computed = computeErrors();
    setErrors(computed);
    setValidationAttempted(true);

    const problems = countProblems(computed);
    if (problems > 0) {
      setFocusErrorsNonce(n => n + 1);
      toast.current?.show({
        severity: 'warn',
        summary: 'Please fix the highlighted fields',
        detail: `${problems} field${problems === 1 ? '' : 's'} need${problems === 1 ? 's' : ''} attention.`,
        life: 4000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await handleEditSubmit();
      onHide();
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Save failed',
        detail: 'Failed to save changes. Please try again.',
        life: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (newData: Partial<FormState>) => {
    setEditFormData(newData);
  };

  const updateItemField = (
    index: number,
    field: keyof EditablePurchaseOrderItem | 'material',
    value: any
  ) => {
    if (!editFormData.items) return;

    const updatedItems = [...editFormData.items];
    if (field === 'product' && typeof value === 'object' && value !== null) {
      // When product is selected, update product, productId, material, and unitPrice
      updatedItems[index] = {
        ...updatedItems[index],
        product: value,
        productId: value.id,
        material: value.id.toString(),
        unitPrice: value.basePrice?.toString() || updatedItems[index].unitPrice,
      };
    } else if (field === 'material' && typeof value === 'string') {
      const materialId = value.trim();
      const productId = parseInt(materialId) || 0;
      const matchingProduct = filteredProducts.find(p => p.id === productId);

      if (matchingProduct) {
        updatedItems[index] = {
          ...updatedItems[index],
          material: materialId,
          productId: productId,
          product: matchingProduct,
          unitPrice: matchingProduct.basePrice?.toString() || updatedItems[index].unitPrice,
        };
      } else {
        // No product matched locally. Keep the typed material string, clear any
        // stale product, and leave productId 0 — the backend resolves it by name
        // or creates a new product for a new material.
        updatedItems[index] = {
          ...updatedItems[index],
          material: materialId,
          productId: 0,
          product: undefined,
        };
      }
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
      };
    }

    updateFormData({
      ...editFormData,
      items: updatedItems,
    });
  };

  const updateHandlingUnitField = (
    itemIndex: number,
    huIndex: number,
    field: keyof EditableHandlingUnit,
    value: any
  ) => {
    if (!editFormData.items) return;

    const updatedItems = [...editFormData.items];
    const item = updatedItems[itemIndex];
    if (item && item.handlingUnits) {
      const updatedHUs = [...item.handlingUnits];
      updatedHUs[huIndex] = {
        ...updatedHUs[huIndex],
        [field]: value,
      };
      item.handlingUnits = updatedHUs;
      item.hu = updatedHUs.map(hu => hu.huNumber);

      updateFormData({
        ...editFormData,
        items: updatedItems,
      });
    }
  };

  const removeItem = (index: number) => {
    if (!editFormData.items) return;
    const updatedItems = [...editFormData.items];
    updatedItems.splice(index, 1);
    updateFormData({
      ...editFormData,
      items: updatedItems,
    });
  };

  const addItem = (item: EditablePurchaseOrderItem) => {
    const updatedItems = [...(editFormData.items || []), item];
    updateFormData({
      ...editFormData,
      items: updatedItems,
    });
  };

  const isDirty = visible && JSON.stringify(editFormData) !== initialSnapshotRef.current;

  const handleRequestClose = () => {
    if (isSubmitting) return;
    if (isDirty) {
      confirmDialog({
        message: 'You have unsaved changes. Are you sure you want to discard them?',
        header: 'Discard changes?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard',
        rejectLabel: 'Keep editing',
        acceptClassName: 'p-button-danger',
        accept: onHide,
      });
    } else {
      onHide();
    }
  };

  const footer = (
    <div className="flex align-items-center justify-content-end gap-2">
      {isDirty && (
        <span className="text-color-secondary text-sm mr-auto flex align-items-center gap-1">
          <i className="pi pi-circle-fill text-orange-500" style={{ fontSize: '0.6rem' }} />
          Unsaved changes
        </span>
      )}
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={handleRequestClose}
        className="p-button-text"
        disabled={isSubmitting}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        onClick={handleSubmit}
        loading={isSubmitting}
        autoFocus
      />
    </div>
  );

  const itemCount = editFormData.items?.length || 0;
  const header = (
    <div className="flex align-items-center gap-2">
      <span>Edit Purchase Order</span>
      {itemCount > 0 && <Badge value={itemCount} severity="info" />}
      {isDirty && (
        <span className="text-orange-500 text-sm font-normal flex align-items-center gap-1">
          <i className="pi pi-circle-fill" style={{ fontSize: '0.6rem' }} />
          Unsaved
        </span>
      )}
    </div>
  );

  const headerErrors = {
    poNumber: errors.poNumber,
    notes: errors.notes,
  };

  return (
    <Dialog
      header={header}
      visible={visible}
      onHide={handleRequestClose}
      style={{ width: '92vw', maxWidth: '1200px' }}
      contentStyle={{ maxHeight: '75vh' }}
      footer={footer}
      modal
      className="p-fluid"
      closeOnEscape={!isSubmitting}
      closable={!isSubmitting}
    >
      <Toast ref={toast} />
      <ConfirmDialog />

      <DialogHeader
        formData={editFormData}
        onUpdateFormData={updateFormData}
        errors={headerErrors}
      />

      {errors.itemsGeneral && (
        <div className="p-error mb-2">{errors.itemsGeneral}</div>
      )}

      <ItemsTable
        items={editFormData.items || []}
        purchaseOrderId={editFormData.id || 0}
        filteredProducts={filteredProducts}
        newHU={newHU}
        itemErrors={errors.items}
        focusErrorsNonce={focusErrorsNonce}
        onUpdateItem={updateItemField}
        onRemoveItem={removeItem}
        onAddItem={addItem}
        onSearchProducts={searchProducts}
        onUpdateHandlingUnit={updateHandlingUnitField}
        onRemoveHandlingUnit={handleRemoveHU}
        onAddHandlingUnit={handleAddHU}
        onNewHUChange={(huNumber) => setNewHU({ huNumber })}
      />
    </Dialog>
  );
};

export default EditPurchaseOrderDialog;
